import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Autocomplete, TextField, CircularProgress, Chip } from "@mui/material";
import { Controller } from "react-hook-form";
import axios from "axios";
import { useSelector } from "react-redux";
import { appendTeamMemberLawyerId, debounce } from "../../../../styles/styles";
import { backend_api } from "../../../../static/server";

const UserApiField = ({
  name,
  label = "User",
  control,
  value,
  onChange,
  errors,
  isRequired = false,
  apiUrl = `${backend_api}team/create-employee/?is_active=True`,
  showSelfOption = false,
  selfOptionLabel = "Me (Self)",
  defaultUser = null,
  searchKey = "search",
  disabled = false,
  returnFullData = false,
  placeholder = "Select user",
  multiple = false, // new prop for multi-select
  teamMemberLawyerId = null,
}) => {
  const { token } = useSelector((state) => state.user);

  const selfOption = useMemo(
    () => ({
      id: "self",
      first_name: selfOptionLabel,
      last_name: "",
      is_self: true,
    }),
    [selfOptionLabel],
  );

  const [options, setOptions] = useState(() => {
    const initial = [];
    if (showSelfOption) initial.push(selfOption);
    if (defaultUser) {
      if (multiple && Array.isArray(defaultUser)) {
        initial.push(...defaultUser);
      } else if (!multiple && defaultUser) {
        initial.push(defaultUser);
      }
    }
    return initial;
  });
  const [nextUrl, setNextUrl] = useState(apiUrl);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isSelected, setIsSelected] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const lastSearchRef = useRef("");
  const buildUserUrl = useCallback(
    (baseUrl, searchValue = null) => {
      let resolvedUrl = appendTeamMemberLawyerId(baseUrl, teamMemberLawyerId);

      if (searchValue !== null && searchValue !== undefined) {
        const separator = resolvedUrl.includes("?") ? "&" : "?";
        resolvedUrl = `${resolvedUrl}${separator}${searchKey}=${encodeURIComponent(searchValue)}`;
      }

      return resolvedUrl;
    },
    [searchKey, teamMemberLawyerId],
  );
  const hasExternalValue = value !== undefined;
  const [internalValue, setInternalValue] = useState(() => {
    if (hasExternalValue) return value;
    if (defaultUser) {
      if (multiple) {
        return returnFullData
          ? Array.isArray(defaultUser)
            ? defaultUser
            : [defaultUser]
          : Array.isArray(defaultUser)
            ? defaultUser.map((u) => u.id)
            : [defaultUser.id];
      } else {
        return returnFullData ? defaultUser : defaultUser?.id || null;
      }
    }
    return multiple ? [] : null;
  });

  const fetchUsers = useCallback(
    async (url) => {
      if (!url || loading) return;
      setLoading(true);
      try {
        const res = await axios.get(url, {
          headers: { Authorization: `Token ${token}` },
        });

        const results = res.data.results || [];

        setOptions((prev) => {
          if (url === buildUserUrl(apiUrl, searchText)) {
            // Fresh search – reset list (but keep self option if enabled)
            return showSelfOption ? [selfOption, ...results] : results;
          } else {
            // Pagination – append new results
            return [...prev, ...results];
          }
        });
        setNextUrl(res.data.next);
      } catch (err) {
        console.error("Failed to load users", err);
      } finally {
        setLoading(false);
      }
    },
    [
      apiUrl,
      buildUserUrl,
      loading,
      searchText,
      showSelfOption,
      selfOption,
      token,
    ],
  );

  useEffect(() => {
    if (defaultUser) {
      setOptions((prev) => {
        const defaults =
          multiple && Array.isArray(defaultUser) ? defaultUser : [defaultUser];
        const newOptions = [...prev];
        defaults.forEach((user) => {
          if (!newOptions.some((o) => o.id === user.id)) {
            newOptions.push(user);
          }
        });
        return newOptions;
      });
    }
  }, [defaultUser, multiple]);

  useEffect(() => {
    if (hasExternalValue) return;
    if (defaultUser) {
      if (multiple) {
        setInternalValue(
          returnFullData
            ? Array.isArray(defaultUser)
              ? defaultUser
              : [defaultUser]
            : Array.isArray(defaultUser)
              ? defaultUser.map((u) => u.id)
              : [defaultUser.id],
        );
      } else {
        setInternalValue(
          returnFullData ? defaultUser : defaultUser?.id || null,
        );
      }
    }
  }, [defaultUser, hasExternalValue, returnFullData, multiple]);

  useEffect(() => {
    if (!isSelected && searchText !== lastSearchRef.current) {
      lastSearchRef.current = searchText;
      fetchUsers(buildUserUrl(apiUrl, searchText));
    }
  }, [apiUrl, buildUserUrl, fetchUsers, isSelected, searchText]);

  const handleScroll = useCallback(
    (event) => {
      if (
        nextUrl &&
        !loading &&
        event.currentTarget.scrollTop + event.currentTarget.clientHeight >=
          event.currentTarget.scrollHeight - 20
      ) {
        fetchUsers(nextUrl);
      }
    },
    [fetchUsers, nextUrl, loading],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        if (!isSelected) {
          setOptions(showSelfOption ? [selfOption] : []);
          setNextUrl(buildUserUrl(apiUrl, value));
          setSearchText(value);
        }
      }, 300),
    [apiUrl, buildUserUrl, isSelected, showSelfOption, selfOption],
  );

  const handleInputChange = (_, value, reason) => {
    if (reason === "input" && !isSelected) {
      setInputValue(value || "");
      debouncedSearch(value || "");
      return;
    }

    if (reason === "clear") {
      setInputValue("");
    }
  };

  const handleSelection = (selectedOption, fieldOnChange) => {
    let normalizedValue;
    if (multiple) {
      // selectedOption is an array
      normalizedValue = selectedOption
        ? selectedOption
            .map((opt) => {
              if (opt?.is_self) return null;
              return returnFullData ? opt : opt?.id;
            })
            .filter((v) => v !== null)
        : [];
    } else {
      const isSelf = Boolean(selectedOption?.is_self);
      normalizedValue = isSelf
        ? null
        : returnFullData
          ? selectedOption || null
          : selectedOption?.id || null;
    }

    setIsSelected(!!selectedOption && (!multiple || selectedOption.length > 0));
    setInputValue(multiple ? "" : getOptionLabel(selectedOption));

    if (!hasExternalValue && !control) {
      setInternalValue(normalizedValue);
    }

    if (typeof fieldOnChange === "function") {
      fieldOnChange(normalizedValue);
    }

    if (onChange) {
      onChange(normalizedValue, selectedOption || null);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsSelected(false);
    setInputValue("");
    if (options.length === 0 || (showSelfOption && options.length === 1)) {
      setSearchText("");
      const initialUrl = buildUserUrl(apiUrl);
      setNextUrl(initialUrl);
      fetchUsers(initialUrl);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSelected(false);
  };

  const getOptionLabel = useCallback((option) => {
    if (!option) return "";
    if (option.is_self) return option.first_name;
    return (
      `${option.first_name || ""} ${option.last_name || ""}`.trim() ||
      option.reminder_mail ||
      ""
    );
  }, []);

  const findSelectedOption = useCallback((fieldValue) => {
    if (multiple) {
      if (!fieldValue || !Array.isArray(fieldValue)) return [];
      return fieldValue
        .map((val) => {
          if (val === null) return null;
          if (typeof val === "object") {
            if (val.is_self) return selfOption;
            const existing = options.find((o) => o.id === val.id);
            return existing || val;
          }
          const fromOptions = options.find((o) => o.id === val);
          if (fromOptions) return fromOptions;
          if (defaultUser) {
            const defaults = Array.isArray(defaultUser)
              ? defaultUser
              : [defaultUser];
            const found = defaults.find((u) => u.id === val);
            if (found) return found;
          }
          return null;
        })
        .filter((v) => v !== null);
    } else {
      if (fieldValue === null || fieldValue === undefined) {
        return null;
      }
      if (fieldValue === "self") return selfOption;
      if (typeof fieldValue === "object") {
        if (fieldValue.is_self) return selfOption;
        const existing = options.find((o) => o.id === fieldValue.id);
        return existing || fieldValue;
      }
      const fromOptions = options.find((o) => o.id === fieldValue);
      if (fromOptions) return fromOptions;
      if (defaultUser && defaultUser.id === fieldValue) return defaultUser;
      return null;
    }
  }, [defaultUser, multiple, options, selfOption]);

  const resolvedValue = hasExternalValue ? value : internalValue;

  useEffect(() => {
    if (isOpen) return;
    const selectedValue = findSelectedOption(resolvedValue);
    setInputValue(multiple ? "" : getOptionLabel(selectedValue));
  }, [findSelectedOption, getOptionLabel, isOpen, multiple, resolvedValue]);

  // Common Autocomplete props
  const autocompleteProps = {
    disabled,
    options,
    getOptionLabel,
    inputValue,
    onInputChange: handleInputChange,
    onOpen: handleOpen,
    onClose: handleClose,
    disableCloseOnSelect: multiple,
    filterSelectedOptions: multiple,
    ListboxProps: {
      onScroll: handleScroll,
      style: { maxHeight: 250, overflowY: "auto" },
    },
    loading,
    isOptionEqualToValue: (opt, val) => {
      if (!opt || !val) return false;
      if (opt?.is_self && val?.is_self) return true;
      return opt?.id === val?.id;
    },
    renderOption: (props, option) => (
      <li {...props} key={option.id}>
        {option.is_self ? (
          <span style={{ fontWeight: "500" }}>{getOptionLabel(option)}</span>
        ) : (
          getOptionLabel(option)
        )}
      </li>
    ),
    renderInput: (params) => (
      <TextField
        {...params}
        label={
          <span>
            {label}
            {isRequired && <span style={{ color: "red" }}> *</span>}
          </span>
        }
        placeholder={placeholder}
        size="small"
        error={Boolean(errors?.[name])}
        helperText={errors?.[name]?.message}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    ),
  };

  if (multiple) {
    autocompleteProps.multiple = true;
    autocompleteProps.renderTags = (value, getTagProps) =>
      value.map((option, index) => (
        <Chip
          label={getOptionLabel(option)}
          size="small"
          {...getTagProps({ index })}
        />
      ));
  }

  return (
    <>
      {control && name ? (
        <Controller
          name={name}
          control={control}
          defaultValue={
            multiple
              ? returnFullData
                ? Array.isArray(defaultUser)
                  ? defaultUser
                  : defaultUser
                    ? [defaultUser]
                    : []
                : Array.isArray(defaultUser)
                  ? defaultUser.map((u) => u.id)
                  : defaultUser
                    ? [defaultUser.id]
                    : []
              : returnFullData
                ? defaultUser || null
                : defaultUser?.id || null
          }
          rules={isRequired ? { required: `${label} is required` } : {}}
          render={({
            field: {
              ref,
              value: fieldValue,
              onChange: fieldOnChange,
              onBlur,
              ...restField
            },
          }) => (
            <Autocomplete
              {...restField}
              {...autocompleteProps}
              value={findSelectedOption(fieldValue)}
              onChange={(_, val) => handleSelection(val, fieldOnChange)}
              onBlur={onBlur}
            />
          )}
        />
      ) : (
        <Autocomplete
          {...autocompleteProps}
          value={findSelectedOption(resolvedValue)}
          onChange={(_, val) => handleSelection(val)}
        />
      )}
    </>
  );
};

export default UserApiField;
